import { Badge, Button, Card, CardBody, DataTable, Tooltip, Modal, CodeDisplay } from '@aidkitorg/component-library';
import React, { useState } from 'react';
import { useNotifications } from '../../../Contexts/NotificationsContext';
import type { NotificationSummary } from '@aidkitorg/aidkit/lib/notifications/NotificationService';
import { TableAction } from '@aidkitorg/component-library/dist/types/src/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { Filters, NotificationsFilter } from './NotificationsFilter';
import { useHistory, useLocation } from 'react-router-dom';
import { useVisibilityPolling } from '../../../Hooks/VisibilityPolling';
import { useLocalizedStrings } from '../../../Localization';
import { Icon } from '@aidkitorg/component-library';

export const NotificationsDataTable: React.FC<{
  lastUpdatedTimestamp?: number
}> = ({
  lastUpdatedTimestamp
}) => {
  const L = useLocalizedStrings()

  const NOTIFICATION_COLUMNS: ColumnDef<NotificationSummary>[] = [
    {
      accessorKey: 'name',
      header: L.admin_panel.notifications.name,
      size: 200,
      cell: ({ row }) => {
        const targetPrefix = row.original.targetPrefix
        return <div className="flex gap-2 items-center">
          {row.original.expandableRows && (
            <Button
              onClick={row.getToggleExpandedHandler()}
              variant="unstyled"
              title={row.getIsExpanded() ? 'Collapse' : 'Expand'}
            >
              {row.getIsExpanded() ? <Icon name="ChevronDownIcon" /> : <Icon name="ChevronRightIcon" />}
            </Button>
          )}
          <Button 
            className="!text-left"
            variant="link"
            onClick={(e) => {
              e.preventDefault()
              const targetPrefix = row.original.targetPrefix
              history.push('/admin-panel/notifications/notification-details/'+targetPrefix)
            }}
            title={row.original.name}
          >
            {row.original.name}
          </Button>
          {row.original.expandableRows && row.original.expandableRows.some(r => r.sqlError) && 
            <Button variant="danger" className="cursor-pointer" title={L.admin_panel.notifications.sql_error} onClick={() => {
              setSelectedNotification(row.original)
              setDetailsModalOpen(true)
            }}>
              <Icon name="ExclamationTriangleIcon" size="lg"/>
            </Button>
          }
        </div>
      }
    },
    {
      accessorKey: 'methods',
      header: L.admin_panel.notifications.channels,
      cell: ({ row }) => {
        const methods = row.original.methods
        return <div className='flex gap-2 items-center px-2'>
          { methods.includes('email') && <Tooltip text={`${L.admin_panel.notifications.source_email} ${row.original.emailSource}`}><Icon className='inline' name="EnvelopeIcon" role="informative" title={L.user.email} /></Tooltip> }
          { methods.includes('sms') && <Tooltip text={`${L.admin_panel.notifications.source_phone} ${row.original.smsSource}`}><Icon className='inline' name="DevicePhoneMobileIcon" role="informative" title={L.user.phone} /></Tooltip> }
          { methods.includes('whatsapp') && <Tooltip text={`${L.admin_panel.notifications.source_whatsapp} ${row.original.smsSource}`}><Icon className='inline' name="WhatsAppIcon" role="informative" title={L.admin_panel.notifications.whatsapp} /></Tooltip> }
        </div>
      }
    },
    {
      accessorKey: "enabledStatus",
      header: L.admin_panel.notifications.notification_status,
      cell: ({ row }) => {
        const notificationEnabledStatus = row.original.enabledStatus
        
        const label = notificationEnabledStatus === 'enabled' ? L.admin_panel.payments.enabled : L.admin_panel.payments.disabled
        const variant = notificationEnabledStatus === 'enabled' ? 'success' : 'neutral'
        return <Badge variant={variant}>{label}</Badge>
      },
    },
    {
      accessorKey: 'sentCount',
      header: L.admin_panel.notifications.sent,
    },
    {
      accessorKey: "eligibleCount",
      header: L.admin_panel.payments.eligible,
      cell: ({ row }) => {
        return row.original.sqlError || (row.original.expandableRows && row.original.expandableRows.some(r => r.sqlError)) ? '--' : row.original.eligibleCount
      }
    },
    {
      accessorKey: "failureCount",
      header: L.admin_panel.notifications.failed,
    },
    {
      accessorKey: "firstSent",
      header: L.admin_panel.notifications.first_sent,
      cell: ({ row }) => {
        if (row.original.firstSent == '') return "--"
        const date = new Date(row.original.firstSent)
        const formatted = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }).format(date)
  
        return <span>{formatted}</span>
      },
      sortingFn: (rowA, rowB) => {
        const a = new Date(rowA.original.firstSent).getTime()
        const b = new Date(rowB.original.firstSent).getTime()
        return a - b
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue?.firstSent || !filterValue?.lastSent) return true
        const date = row.getValue(columnId) as Date
        return (
          date >= new Date(filterValue.firstSent) &&
          date <= new Date(filterValue.lastSent)
        )
      },
    },
    {
      accessorKey: "lastSent",
      header: L.admin_panel.notifications.last_sent,
      cell: ({ row }) => {
        if (row.original.lastSent == '') return "--"
        
        const date = new Date(row.original.lastSent)
        const formatted = new Intl.DateTimeFormat("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        }).format(date)
  
        return <span>{formatted}</span>
      },
      sortingFn: (rowA, rowB) => {
        const a = new Date(rowA.original.lastSent).getTime()
        const b = new Date(rowB.original.lastSent).getTime()
        return a - b
      },
      filterFn: (row, columnId, filterValue) => {
        if (!filterValue?.firstSent || !filterValue?.lastSent) return true
        const date = row.getValue(columnId) as Date
        return (
          date >= new Date(filterValue.firstSent) &&
          date <= new Date(filterValue.lastSent)
        )
      },
    },
  ]

  const { notifications,  fetchNotificationsOverview, isNotificationsLoading} = useNotifications()
  const [filters, setFilters] = useState<Filters>({
    enabledStatus: "",
    firstSent: "",
    lastSent: "",
  })
  const [activeFilters, setActiveFilters] = useState<number>(0)  
  const [detailsModalOpen, setDetailsModalOpen] = useState(false)
  const [selectedNotification, setSelectedNotification] = useState<NotificationSummary | null>(null)
  const history = useHistory()

  const location = useLocation()
  const isOnNotificationsPage = location.pathname === '/admin-panel/notifications'

  const notificationActions: TableAction<NotificationSummary>[] = [
    {
      label: L.admin_panel.notifications.view_recipients,
      onClick: (row: NotificationSummary ) => {
        const targetPrefix = row.targetPrefix
        history.push('/admin-panel/notifications/notification-details/'+targetPrefix)
      },
      icon: 'UserGroupIcon'
    },
    {
      label: L.admin_panel.notifications.view_sql,
      onClick: (row: NotificationSummary) => {
        setSelectedNotification(row)
        setDetailsModalOpen(true)
      },
      icon: 'CodeBracketIcon'
    },
  ]

  useVisibilityPolling({
    onImmediate: () => fetchNotificationsOverview(),
    onInterval: () => fetchNotificationsOverview(),
    isActive: isOnNotificationsPage,
    intervalMs: 2 * 60 * 1000
  })
  
  return (
    <div className="flex flex-col gap-y-1">
      <Card>
        <CardBody >
          <DataTable 
            columns={NOTIFICATION_COLUMNS} 
            data={notifications}
            actions={notificationActions}
            enableSearch
            searchLabel={L.admin_panel.notifications.search_notifications}
            filters={(table) => (
              <NotificationsFilter
                filters={filters}
                setFilters={setFilters}
                table={table}
                setActiveFilters={setActiveFilters}
              />
            )}
            activeFilters={activeFilters}
            loading={isNotificationsLoading}
            getSubRows={(row) => row.expandableRows ?? []}
            lastUpdatedTimestamp={lastUpdatedTimestamp}
            dataFilteredOutMessage={L.admin_panel.notifications.no_notifications_match_filter}
            noDataMessage={L.admin_panel.notifications.no_notifications_configured}
          />
        </CardBody>
      </Card>
      
      {/* Unified Details Modal (Queries + Errors) */}
      <Modal 
        open={detailsModalOpen} 
        onClose={setDetailsModalOpen}
        title={`${L.admin_panel.notifications.sql_details} - ${selectedNotification?.name}`}
        size="fullscreen"
      >
        {selectedNotification && (
          <div className="space-y-6">
            <div className="text-sm text-gray-600 mb-4">
              {L.admin_panel.notifications.sql_details_description}
            </div>
            
            {/* Method queries */}
            {selectedNotification.sqlQueries && selectedNotification.sqlQueries.length > 0 && (
              <div>
                <div className="space-y-10 ">
                  {selectedNotification.sqlQueries.map((query, index) => {
                    // Find corresponding error for failed queries
                    const errorInfo = !query.success ? (
                      selectedNotification.expandableRows?.find(row => 
                        row.methods.includes(query.method as any)
                      )?.sqlError
                    ) : undefined;
                    
                    return (
                      <div key={index}>
                        <div className="flex items-center justify-between">
                          <p className="font-semibold">
                            {query.method}
                          </p>
                          <Badge variant={query.success ? 'success' : 'neutral'}>
                            {query.success ? L.admin_panel.notifications.success : L.admin_panel.notifications.failed}
                          </Badge>
                        </div>
                        
                        {/* Show error message for failed queries */}
                        {!query.success && errorInfo && (
                          <div className="mb-3 p-3 bg-red-100 border border-red-200 rounded">
                            <p className="font-semibold text-red-800 mb-1">{L.admin_panel.notifications.error_message}:</p>
                            <p className="text-red-700">{errorInfo}</p>
                          </div>
                        )}
                        
                        <div className="space-y-3">
                          <div>
                            <p className="font-semibold mb-1">{L.admin_panel.notifications.query}:</p>
                            <CodeDisplay code={query.query} language="sql" expandable={false} copyable={false} showLineNumbers={false} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div className="flex justify-end pt-4">
              <Button onClick={() => setDetailsModalOpen(false)}>
                {L.admin_panel.notifications.close}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}